/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationEvent, AnimationMetadataType, AnimationPlayer, AnimationStateMetadata, AnimationTriggerMetadata, ɵStyleData} from '@angular/animations';

import {AnimationEngine} from '../animation_engine';
import {copyStyles, eraseStyles, normalizeStyles, setStyles} from '../util';

interface ListenerTuple {
  eventPhase: string;
  triggerName: string;
  callback: (event: any) => any;
  doRemove?: boolean;
}

interface ChangeTuple {
  element: any;
  triggerName: string;
  oldValue: string;
  newValue: string;
}

const /** @type {?} */ DEFAULT_STATE_VALUE = 'void';
const /** @type {?} */ DEFAULT_STATE_STYLES = '*';
export class NoopAnimationEngine extends AnimationEngine {
private _listeners = new Map<any, ListenerTuple[]>();
private _changes: ChangeTuple[] = [];
private _flaggedRemovals = new Set<any>();
private _onDoneFns: (() => any)[] = [];
private _triggerStyles: {[triggerName: string]: {[stateName: string]: ɵStyleData}} =
      Object.create(null);
/**
 * @param {?} trigger
 * @param {?=} name
 * @return {?}
 */
registerTrigger(trigger: AnimationTriggerMetadata, name?: string): void {
    name = name || trigger.name;
    if (this._triggerStyles[name]) {
      return;
    }
    const /** @type {?} */ stateMap: {[stateName: string]: ɵStyleData} = {};
    trigger.definitions.forEach(def => {
      if (def.type === AnimationMetadataType.State) {
        const /** @type {?} */ stateDef = /** @type {?} */(( def as AnimationStateMetadata));
        stateMap[stateDef.name] = normalizeStyles(stateDef.styles.styles);
      }
    });
    this._triggerStyles[name] = stateMap;
  }
/**
 * @param {?} element
 * @param {?} domFn
 * @return {?}
 */
onInsert(element: any, domFn: () => any): void { domFn(); }
/**
 * @param {?} element
 * @param {?} domFn
 * @return {?}
 */
onRemove(element: any, domFn: () => any): void {
    domFn();
    if (element['nodeType'] == 1) {
      this._flaggedRemovals.add(element);
    }
  }
/**
 * @param {?} element
 * @param {?} property
 * @param {?} value
 * @return {?}
 */
setProperty(element: any, property: string, value: any): void {
    const /** @type {?} */ storageProp = makeStorageProp(property);
    const /** @type {?} */ oldValue = element[storageProp] || DEFAULT_STATE_VALUE;
    this._changes.push( /** @type {?} */((<ChangeTuple>{element, oldValue, newValue: value, triggerName: property})));

    const /** @type {?} */ triggerStateStyles = this._triggerStyles[property] || {};
    const /** @type {?} */ fromStateStyles =
        triggerStateStyles[oldValue] || triggerStateStyles[DEFAULT_STATE_STYLES];
    if (fromStateStyles) {
      eraseStyles(element, fromStateStyles);
    }

    element[storageProp] = value;
    this._onDoneFns.push(() => {
      const /** @type {?} */ toStateStyles = triggerStateStyles[value] || triggerStateStyles[DEFAULT_STATE_STYLES];
      if (toStateStyles) {
        setStyles(element, toStateStyles);
      }
    });
  }
/**
 * @param {?} element
 * @param {?} eventName
 * @param {?} eventPhase
 * @param {?} callback
 * @return {?}
 */
listen(element: any, eventName: string, eventPhase: string, callback: (event: any) => any):
      () => any {
    let /** @type {?} */ listeners = this._listeners.get(element);
    if (!listeners) {
      this._listeners.set(element, listeners = []);
    }

    const /** @type {?} */ tuple = /** @type {?} */(( <ListenerTuple>{triggerName: eventName, eventPhase, callback}));
    listeners.push(tuple);

    return () => tuple.doRemove = true;
  }
/**
 * @return {?}
 */
flush(): void {
    const /** @type {?} */ onStartCallbacks: (() => any)[] = [];
    const /** @type {?} */ onDoneCallbacks: (() => any)[] = [];
/**
 * @param {?} listener
 * @param {?} data
 * @return {?}
 */
function handleListener(listener: ListenerTuple, data: ChangeTuple) {
      const /** @type {?} */ phase = listener.eventPhase;
      const /** @type {?} */ event = makeAnimationEvent(
          data.element, data.triggerName, data.oldValue, data.newValue, phase, 0);
      if (phase == 'start') {
        onStartCallbacks.push(() => listener.callback(event));
      } else if (phase == 'done') {
        onDoneCallbacks.push(() => listener.callback(event));
      }
    }

    this._changes.forEach(change => {
      const /** @type {?} */ element = change.element;
      const /** @type {?} */ listeners = this._listeners.get(element);
      if (listeners) {
        listeners.forEach(listener => {
          if (listener.triggerName == change.triggerName) {
            handleListener(listener, change);
          }
        });
      }
    });

    // upon removal ALL the animation triggers need to get fired
    this._flaggedRemovals.forEach(element => {
      const /** @type {?} */ listeners = this._listeners.get(element);
      if (listeners) {
        listeners.forEach(listener => {
          const /** @type {?} */ triggerName = listener.triggerName;
          const /** @type {?} */ storageProp = makeStorageProp(triggerName);
          handleListener(listener, /** @type {?} */(( <ChangeTuple>{
            element: element,
            triggerName: triggerName,
            oldValue: element[storageProp] || DEFAULT_STATE_VALUE,
            newValue: DEFAULT_STATE_VALUE
          })));
        });
      }
    });

    // remove all the listeners after everything is complete
    Array.from(this._listeners.keys()).forEach(element => {
      const /** @type {?} */ listenersToKeep = /** @type {?} */(( this._listeners.get(element))).filter(l => !l.doRemove);
      if (listenersToKeep.length) {
        this._listeners.set(element, listenersToKeep);
      } else {
        this._listeners.delete(element);
      }
    });

    onStartCallbacks.forEach(fn => fn());
    onDoneCallbacks.forEach(fn => fn());
    this._flaggedRemovals.clear();
    this._changes = [];

    this._onDoneFns.forEach(doneFn => doneFn());
    this._onDoneFns = [];
  }
/**
 * @return {?}
 */
get activePlayers(): AnimationPlayer[] { return []; }
/**
 * @return {?}
 */
get queuedPlayers(): AnimationPlayer[] { return []; }
}

function NoopAnimationEngine_tsickle_Closure_declarations() {
/** @type {?} */
NoopAnimationEngine.prototype._listeners;
/** @type {?} */
NoopAnimationEngine.prototype._changes;
/** @type {?} */
NoopAnimationEngine.prototype._flaggedRemovals;
/** @type {?} */
NoopAnimationEngine.prototype._onDoneFns;
/** @type {?} */
NoopAnimationEngine.prototype._triggerStyles;
}

/**
 * @param {?} element
 * @param {?} triggerName
 * @param {?} fromState
 * @param {?} toState
 * @param {?} phaseName
 * @param {?} totalTime
 * @return {?}
 */
function makeAnimationEvent(
    element: any, triggerName: string, fromState: string, toState: string, phaseName: string,
    totalTime: number): AnimationEvent {
  return /** @type {?} */(( <AnimationEvent>{element, triggerName, fromState, toState, phaseName, totalTime}));
}
/**
 * @param {?} property
 * @return {?}
 */
function makeStorageProp(property: string): string {
  return '_@_' + property;
}
